import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../common/Table';
import Button from '../common/Button';
import Card from '../common/Card';
import Tabs from '../common/Tabs';
import Pagination from '../common/Pagination';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/debt-list.css';

const DebtList = () => {
  const navigate = useNavigate();
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [summary, setSummary] = useState({
    active_count: 0,
    overdue_count: 0,
    settled_count: 0,
    active_amount: 0,
    overdue_amount: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadDebts();
    loadSummary();
  }, [activeTab, pagination.page]);

  const loadDebts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/debts.php', {
        params: {
          status: activeTab,
          page: pagination.page,
          limit: pagination.limit
        }
      });
      setDebts(response.data.debts);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await api.get('/debts.php/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load debt summary:', error);
    }
  };

  const handleRecordPayment = (debt) => {
    navigate(`/debts/payment`, { state: { debt } });
  };

  const tabs = [
    { id: 'active', label: 'Active', count: summary.active_count },
    { id: 'overdue', label: 'Overdue', count: summary.overdue_count },
    { id: 'settled', label: 'Settled', count: summary.settled_count },
    { id: 'all', label: 'All' }
  ];

  const columns = [
    {
      key: 'customer',
      label: 'Customer',
      render: (_, row) => (
        <div className="customer-info">
          <div className="customer-name">
            {row.first_name} {row.last_name}
          </div>
          <div className="customer-phone">{row.phone}</div>
        </div>
      )
    },
    {
      key: 'invoice_number',
      label: 'Invoice'
    },
    {
      key: 'sale_date',
      label: 'Sale Date',
      render: (value) => formatDate(value)
    },
    {
      key: 'original_amount',
      label: 'Original',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'remaining_amount',
      label: 'Remaining',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (value, row) => (
        <div>
          <div>{formatDate(value)}</div>
          {row.status === 'overdue' && (
            <div className="overdue-badge">Overdue</div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => navigate(`/debts/${row.id}`)}
          >
            View
          </Button>
          {row.status !== 'settled' && (
            <Button 
              size="sm" 
              variant="primary"
              onClick={() => handleRecordPayment(row)}
            >
              Pay
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="debt-list">
      <Card>
        <div className="debt-list-header">
          <h2>Credit Sales & Debts</h2>
          <div className="summary-cards">
            <div className="summary-card">
              <span className="summary-label">Total Outstanding</span>
              <span className="summary-value">
                {formatCurrency(summary.active_amount + summary.overdue_amount)}
              </span>
            </div>
            <div className="summary-card warning">
              <span className="summary-label">Overdue</span>
              <span className="summary-value">{formatCurrency(summary.overdue_amount)}</span>
            </div>
          </div>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        <Table
          columns={columns}
          data={debts}
          loading={loading}
          onRowClick={(row) => navigate(`/debts/${row.id}`)}
        />

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          onPageChange={(page) => setPagination({ ...pagination, page })}
        />
      </Card>
    </div>
  );
};

export default DebtList;